/**
 * Ultrasound Cache Management
 * 
 * Centralized cache management for ultrasound services with performance optimization,
 * automatic cleanup, and comprehensive monitoring capabilities.
 */

import { UltrasoundError, UltrasoundErrorType } from './types';

// Cache configuration - 1 minute TTL for ultrasound operations
const CACHE_TTL = 60 * 1000; // 1 minute in milliseconds

// Cache performance configuration
const CACHE_PERFORMANCE_CONFIG = {
    maxEntries: 1000,           // Maximum number of cache entries
    cleanupInterval: 5 * 60 * 1000, // Cleanup every 5 minutes
    memoryThreshold: 50 * 1024 * 1024, // 50MB memory threshold
    enableHitMissLogging: true,  // Enable cache hit/miss logging
    logInterval: 60 * 1000      // Log performance stats every minute
};

/**
 * Cache entry interface for typed cache storage with size tracking
 */
interface CacheEntry<T> {
    data: T;
    timestamp: number;
    size: number;        // Estimated size in bytes
    accessCount: number; // Number of times accessed
    lastAccessed: number; // Last access timestamp
}

/**
 * Cache performance metrics for monitoring
 */
interface CachePerformanceMetrics {
    hits: number;
    misses: number;
    totalRequests: number;
    hitRate: number;
    memoryUsage: number;
    entryCount: number;
    lastCleanup: number;
    collisionCount: number;
}

/**
 * In-memory cache object for ultrasound measurements
 */
const measurementsCache: Record<string, CacheEntry<any>> = {};

/**
 * Cache performance metrics tracking
 */
let cacheMetrics: CachePerformanceMetrics = {
    hits: 0,
    misses: 0,
    totalRequests: 0,
    hitRate: 0,
    memoryUsage: 0,
    entryCount: 0,
    lastCleanup: Date.now(),
    collisionCount: 0
};

/**
 * Cleanup interval reference for automatic cache maintenance
 */
let cleanupIntervalId: NodeJS.Timeout | null = null;

/**
 * Performance logging interval reference
 */
let performanceLogIntervalId: NodeJS.Timeout | null = null;

/**
 * Generates a cache key for ultrasound operations with collision prevention
 * @param operation - The ultrasound operation type
 * @param site - Site identifier
 * @param asset - Asset identifier
 * @param position - Position identifier (optional for some operations)
 * @returns Formatted cache key string with collision prevention
 */
export const getCacheKey = (
    operation: string,
    site: string,
    asset: string,
    position?: string,
): string => {
    // Sanitize inputs to prevent key collisions
    const sanitize = (str: string): string => {
        return str.replace(/[_#]/g, '-').toLowerCase();
    };

    const sanitizedOperation = sanitize(operation);
    const sanitizedSite = sanitize(site);
    const sanitizedAsset = sanitize(asset);
    
    if (position) {
        const sanitizedPosition = sanitize(position);
        return `ults:${sanitizedOperation}:${sanitizedSite}:${sanitizedAsset}:${sanitizedPosition}`;
    }
    return `ults:${sanitizedOperation}:${sanitizedSite}:${sanitizedAsset}`;
};

/**
 * Estimates the memory size of a cache entry in bytes
 * @param data - The data to estimate size for
 * @returns Estimated size in bytes
 */
const estimateDataSize = (data: any): number => {
    try {
        const jsonString = JSON.stringify(data);
        // Rough estimation: 2 bytes per character for UTF-16 encoding
        return jsonString.length * 2;
    } catch (error) {
        // Fallback estimation for non-serializable data
        console.warn('Failed to estimate data size, using fallback:', error);
        return 1024; // 1KB fallback
    }
};

/**
 * Updates cache performance metrics
 * @param isHit - Whether this was a cache hit or miss
 * @param operation - The operation type for logging
 */
const updateCacheMetrics = (isHit: boolean, operation?: string): void => {
    cacheMetrics.totalRequests++;
    
    if (isHit) {
        cacheMetrics.hits++;
    } else {
        cacheMetrics.misses++;
    }
    
    cacheMetrics.hitRate = cacheMetrics.totalRequests > 0 
        ? (cacheMetrics.hits / cacheMetrics.totalRequests) * 100 
        : 0;
    
    cacheMetrics.entryCount = Object.keys(measurementsCache).length;
    
    // Calculate total memory usage
    cacheMetrics.memoryUsage = Object.values(measurementsCache)
        .reduce((total, entry) => total + entry.size, 0);
    
    // Log cache performance if enabled
    if (CACHE_PERFORMANCE_CONFIG.enableHitMissLogging && operation) {
        console.debug(`Cache ${isHit ? 'HIT' : 'MISS'} for ${operation}`, {
            hitRate: cacheMetrics.hitRate.toFixed(2) + '%',
            totalEntries: cacheMetrics.entryCount,
            memoryUsage: (cacheMetrics.memoryUsage / 1024).toFixed(2) + 'KB'
        });
    }
};

/**
 * Performs automatic cache cleanup based on size and memory limits
 * @returns Number of entries removed
 */
const performCacheCleanup = (): number => {
    try {
        let removedCount = 0;
        const currentTime = Date.now();
        const cacheKeys = Object.keys(measurementsCache);
        
        // First, remove expired entries
        cacheKeys.forEach(key => {
            const entry = measurementsCache[key];
            if (entry && (currentTime - entry.timestamp >= CACHE_TTL)) {
                delete measurementsCache[key];
                removedCount++;
            }
        });
        
        // If still over limits, remove least recently used entries
        const remainingKeys = Object.keys(measurementsCache);
        const currentMemoryUsage = Object.values(measurementsCache)
            .reduce((total, entry) => total + entry.size, 0);
        
        if (remainingKeys.length > CACHE_PERFORMANCE_CONFIG.maxEntries || 
            currentMemoryUsage > CACHE_PERFORMANCE_CONFIG.memoryThreshold) {
            
            // Sort by last accessed time (LRU)
            const sortedEntries = remainingKeys
                .map(key => ({ key, entry: measurementsCache[key] }))
                .sort((a, b) => a.entry.lastAccessed - b.entry.lastAccessed);
            
            // Remove entries until under limits
            let currentSize = currentMemoryUsage;
            let currentCount = remainingKeys.length;
            
            for (const { key, entry } of sortedEntries) {
                if (currentCount <= CACHE_PERFORMANCE_CONFIG.maxEntries && 
                    currentSize <= CACHE_PERFORMANCE_CONFIG.memoryThreshold) {
                    break;
                }
                
                delete measurementsCache[key];
                currentSize -= entry.size;
                currentCount--;
                removedCount++;
            }
        }
        
        cacheMetrics.lastCleanup = currentTime;
        
        if (removedCount > 0) {
            console.debug(`Cache cleanup completed: ${removedCount} entries removed`, {
                remainingEntries: Object.keys(measurementsCache).length,
                memoryUsage: (Object.values(measurementsCache).reduce((total, entry) => total + entry.size, 0) / 1024).toFixed(2) + 'KB'
            });
        }
        
        return removedCount;
    } catch (error) {
        console.warn('Cache cleanup failed:', error);
        return 0;
    }
};

/**
 * Logs cache performance statistics
 */
const logCachePerformance = (): void => {
    if (!CACHE_PERFORMANCE_CONFIG.enableHitMissLogging) {
        return;
    }
    
    try {
        const stats = {
            hitRate: cacheMetrics.hitRate.toFixed(2) + '%',
            totalRequests: cacheMetrics.totalRequests,
            hits: cacheMetrics.hits,
            misses: cacheMetrics.misses,
            entryCount: cacheMetrics.entryCount,
            memoryUsage: (cacheMetrics.memoryUsage / 1024).toFixed(2) + 'KB',
            collisions: cacheMetrics.collisionCount,
            lastCleanup: new Date(cacheMetrics.lastCleanup).toISOString()
        };
        
        console.info('Ultrasound Cache Performance Stats:', stats);
    } catch (error) {
        console.warn('Failed to log cache performance:', error);
    }
};

/**
 * Checks if a cache entry is still valid based on TTL
 * @param entry - Cache entry to validate
 * @returns True if cache entry is still valid, false if expired
 */
export const isCacheValid = (entry: CacheEntry<any>): boolean => {
    return Date.now() - entry.timestamp < CACHE_TTL;
};

/**
 * Safely updates cache with error handling and performance tracking
 * @param key - Cache key
 * @param data - Data to cache
 * @param operation - Operation type for metrics
 */
export const safeUpdateCache = <T>(key: string, data: T, operation?: string): void => {
    try {
        // Check for key collision
        if (measurementsCache[key]) {
            cacheMetrics.collisionCount++;
            console.debug(`Cache key collision detected for: ${key}`);
        }
        
        const currentTime = Date.now();
        const dataSize = estimateDataSize(data);
        
        measurementsCache[key] = {
            data,
            timestamp: currentTime,
            size: dataSize,
            accessCount: 1,
            lastAccessed: currentTime
        };
        
        // Trigger cleanup if over limits
        const currentMemoryUsage = Object.values(measurementsCache)
            .reduce((total, entry) => total + entry.size, 0);
        const currentEntryCount = Object.keys(measurementsCache).length;
        
        if (currentEntryCount > CACHE_PERFORMANCE_CONFIG.maxEntries || 
            currentMemoryUsage > CACHE_PERFORMANCE_CONFIG.memoryThreshold) {
            performCacheCleanup();
        }
        
    } catch (error) {
        // Log cache error but don't fail the operation
        console.warn('Cache update failed for key:', key, error);

        // Optionally throw a non-blocking error for monitoring
        const cacheError = new UltrasoundError(
            UltrasoundErrorType.CACHE_ERROR,
            'Failed to update cache, but operation completed successfully',
            undefined,
            error instanceof Error ? error : undefined
        );

        // Don't throw, just log for monitoring purposes
        console.warn('Cache error (non-blocking):', cacheError);
    }
};

/**
 * Safely retrieves data from cache with error handling and performance tracking
 * @param key - Cache key
 * @param operation - Operation type for metrics
 * @returns Cached data if valid, null if not found or invalid
 */
export const safeGetFromCache = <T>(key: string, operation?: string): T | null => {
    try {
        const cachedData = measurementsCache[key];
        
        if (cachedData && isCacheValid(cachedData)) {
            // Update access tracking
            cachedData.accessCount++;
            cachedData.lastAccessed = Date.now();
            
            // Update metrics for cache hit
            updateCacheMetrics(true, operation);
            
            return cachedData.data;
        }
        
        // Update metrics for cache miss
        updateCacheMetrics(false, operation);
        
        return null;
    } catch (error) {
        // Log cache error but don't fail the operation
        console.warn('Cache retrieval failed for key:', key, error);
        
        // Update metrics for cache miss (error case)
        updateCacheMetrics(false, operation);
        
        return null;
    }
};

/**
 * Initializes automatic cache maintenance
 */
export const initializeCacheManagement = (): void => {
    // Clear any existing intervals
    if (cleanupIntervalId) {
        clearInterval(cleanupIntervalId);
    }
    if (performanceLogIntervalId) {
        clearInterval(performanceLogIntervalId);
    }
    
    // Set up automatic cleanup
    cleanupIntervalId = setInterval(() => {
        performCacheCleanup();
    }, CACHE_PERFORMANCE_CONFIG.cleanupInterval);
    
    // Set up performance logging
    if (CACHE_PERFORMANCE_CONFIG.enableHitMissLogging) {
        performanceLogIntervalId = setInterval(() => {
            logCachePerformance();
        }, CACHE_PERFORMANCE_CONFIG.logInterval);
    }
};

/**
 * Shuts down cache management intervals
 */
export const shutdownCacheManagement = (): void => {
    if (cleanupIntervalId) {
        clearInterval(cleanupIntervalId);
        cleanupIntervalId = null;
    }
    if (performanceLogIntervalId) {
        clearInterval(performanceLogIntervalId);
        performanceLogIntervalId = null;
    }
};

/**
 * Clears ultrasound cache entries based on provided parameters with enhanced pattern matching
 * @param site - Optional site identifier to filter cache entries
 * @param asset - Optional asset identifier to filter cache entries  
 * @param position - Optional position identifier to filter cache entries
 * @throws UltrasoundError if cache clearing fails critically
 */
export const clearCache = (site?: string, asset?: string, position?: string): void => {
    try {
        let clearedCount = 0;
        const cacheKeys = Object.keys(measurementsCache);

        if (site && asset && position) {
            // Clear cache for specific site, asset, and position with exact pattern matching
            const patterns = [
                `ults:ults-baseline-recommendation:${site.replace(/[_#]/g, '-').toLowerCase()}:${asset.replace(/[_#]/g, '-').toLowerCase()}:${position.replace(/[_#]/g, '-').toLowerCase()}`,
                `ults:ults-baseline:${site.replace(/[_#]/g, '-').toLowerCase()}:${asset.replace(/[_#]/g, '-').toLowerCase()}:${position.replace(/[_#]/g, '-').toLowerCase()}`
            ];
            
            cacheKeys.forEach(key => {
                if (patterns.some(pattern => key === pattern)) {
                    delete measurementsCache[key];
                    clearedCount++;
                }
            });
        } else if (site && asset) {
            // Clear cache for specific site and asset with pattern matching
            const sanitizedSite = site.replace(/[_#]/g, '-').toLowerCase();
            const sanitizedAsset = asset.replace(/[_#]/g, '-').toLowerCase();
            const assetPattern = `:${sanitizedSite}:${sanitizedAsset}:`;
            
            cacheKeys.forEach(key => {
                if (key.includes(assetPattern)) {
                    delete measurementsCache[key];
                    clearedCount++;
                }
            });
        } else if (site) {
            // Clear cache for specific site with pattern matching
            const sanitizedSite = site.replace(/[_#]/g, '-').toLowerCase();
            const sitePattern = `:${sanitizedSite}:`;
            
            cacheKeys.forEach(key => {
                if (key.includes(sitePattern)) {
                    delete measurementsCache[key];
                    clearedCount++;
                }
            });
        } else {
            // Clear all ultrasound cache entries
            cacheKeys.forEach(key => {
                if (key.startsWith('ults:')) {
                    delete measurementsCache[key];
                    clearedCount++;
                }
            });
        }

        // Reset metrics if all cache was cleared
        if (!site && !asset && !position) {
            cacheMetrics = {
                hits: 0,
                misses: 0,
                totalRequests: 0,
                hitRate: 0,
                memoryUsage: 0,
                entryCount: 0,
                lastCleanup: Date.now(),
                collisionCount: 0
            };
        }

        console.debug(`Ultrasound cache cleared: ${clearedCount} entries removed`);
    } catch (error) {
        // Log error but don't fail the operation
        console.warn('Cache clearing failed:', error);
        
        // Throw error for critical cache failures
        throw new UltrasoundError(
            UltrasoundErrorType.CACHE_ERROR,
            'Failed to clear ultrasound cache',
            undefined,
            error instanceof Error ? error : undefined
        );
    }
};

/**
 * Gets current cache performance metrics
 * @returns Current cache performance metrics
 */
export const getCacheMetrics = (): CachePerformanceMetrics => {
    return { ...cacheMetrics };
};

/**
 * Gets cache configuration
 * @returns Current cache configuration
 */
export const getCacheConfig = () => {
    return {
        CACHE_TTL,
        CACHE_PERFORMANCE_CONFIG
    };
};